import React, { useState, useEffect } from 'react';
import { Package, DollarSign, AlertTriangle, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import apiService from '../services/api';

function Dashboard({ onClienteClick }) {
  const [stats, setStats] = useState({
    general: {
      totalClientes: 0,
      totalContenedores: 0,
      totalIngresos: 0,
      totalCostos: 0,
      utilidad: 0
    },
    topClientes: [],
    contenedoresActivos: 0,
    demorasAcumuladas: 0,
    alertasCriticas: 0,
    diasPromedio: 0,
    tendenciaMensual: 0,
    navierasActivas: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/dashboard/stats');

      setStats({
        general: {
          totalClientes: data.general?.totalClientes || 0,
          totalContenedores: data.general?.totalContenedores || 0,
          totalIngresos: data.general?.totalIngresos || 0,
          totalCostos: data.general?.totalCostos || 0,
          utilidad: data.general?.utilidad || 0
        },
        topClientes: data.topClientes || [],
        contenedoresActivos: data.contenedoresActivos || data.general?.totalContenedores || 0,
        demorasAcumuladas: data.demorasAcumuladas || data.general?.totalCostos || 0,
        alertasCriticas: data.alertasCriticas || 0,
        diasPromedio: data.diasPromedio || 0,
        tendenciaMensual: data.tendenciaMensual || 0,
        navierasActivas: data.navierasActivas || 0
      });
    } catch (error) {
      console.error('Error al cargar datos:', error);
      // El error 401 es manejado automáticamente por apiService
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-800 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-ink-400">Cargando datos</p>
        </div>
      </div>
    );
  }

  const tendenciaPositiva = (stats.tendenciaMensual || 0) >= 0;

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="mb-10">
        <h1 className="font-display text-3xl text-ink-900 mb-1">Dashboard</h1>
        <p className="text-ink-400">Resumen ejecutivo de operaciones</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {/* Contenedores */}
        <div className="bg-white border-l-4 border-ink-900 shadow-soft rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Package size={32} className="text-ink-700" />
          </div>
          <p className="text-ink-500 text-sm font-medium">Contenedores activos</p>
          <p className="text-3xl font-bold text-ink-900 mt-1">{stats.contenedoresActivos}</p>
          <p className="text-ink-400 text-xs mt-2">{stats.navierasActivas} navieras</p>
        </div>

        {/* Demoras */}
        <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-red-500">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="text-red-600" size={24} />
            <p className="text-gray-700 font-semibold">Demoras acumuladas</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {formatCurrency(stats.demorasAcumuladas)}
          </p>
          <p className="text-gray-600 text-sm mt-2">{stats.diasPromedio} días prom.</p>
        </div>

        {/* Alertas */}
        <div className={`bg-white rounded-lg p-6 shadow-sm border-l-4 ${stats.alertasCriticas > 0 ? 'border-amber-500' : 'border-gray-300'}`}>
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className={stats.alertasCriticas > 0 ? 'text-amber-600' : 'text-gray-400'} size={24} />
            <p className="text-gray-700 font-semibold">Alertas críticas</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {stats.alertasCriticas}
          </p>
          {stats.alertasCriticas > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse-soft" />
              <span className="text-amber-600 text-sm">Requiere atención</span>
            </div>
          )}
        </div>

        {/* Tendencia */}
        <div className={`bg-white rounded-lg p-6 shadow-sm border-l-4 ${tendenciaPositiva ? 'border-green-500' : 'border-red-500'}`}>
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className={tendenciaPositiva ? 'text-green-600' : 'text-red-600'} size={24} />
            <p className="text-gray-700 font-semibold">Tendencia mensual</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {tendenciaPositiva ? '+' : ''}{(stats.tendenciaMensual || 0).toFixed(1)}%
          </p>
          <div className={`flex items-center gap-1 text-sm mt-2 ${tendenciaPositiva ? 'text-green-600' : 'text-red-600'}`}>
            {tendenciaPositiva ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            vs. mes anterior
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        {/* Financial Summary */}
        <div className="bg-white border border-ink-100 rounded-xl p-6">
          <h3 className="text-sm font-medium text-ink-500 uppercase tracking-wider mb-5">
            Resumen financiero
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-ink-50">
              <span className="text-sm text-ink-600">Ingresos totales</span>
              <span className="text-sm font-semibold text-ink-900">
                {formatCurrency(stats.general.totalIngresos)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-ink-50">
              <span className="text-sm text-ink-600">Costos por demoras</span>
              <span className="text-sm font-semibold text-red-600">
                {formatCurrency(stats.general.totalCostos)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm font-medium text-ink-800">Utilidad neta</span>
              <span className="text-lg font-semibold text-emerald-600">
                {formatCurrency(stats.general.utilidad)}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white border border-ink-100 rounded-xl p-6">
          <h3 className="text-sm font-medium text-ink-500 uppercase tracking-wider mb-5">
            Estadísticas
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-ink-50 rounded-lg p-4">
              <p className="text-2xl font-semibold text-ink-900">{stats.general.totalClientes}</p>
              <p className="text-xs text-ink-500 mt-1">Clientes</p>
            </div>
            <div className="bg-ink-50 rounded-lg p-4">
              <p className="text-2xl font-semibold text-ink-900">{stats.general.totalContenedores}</p>
              <p className="text-xs text-ink-500 mt-1">Contenedores</p>
            </div>
            <div className="bg-ink-50 rounded-lg p-4">
              <p className="text-2xl font-semibold text-ink-900">{stats.navierasActivas}</p>
              <p className="text-xs text-ink-500 mt-1">Navieras</p>
            </div>
            <div className="bg-ink-50 rounded-lg p-4">
              <p className="text-2xl font-semibold text-ink-900">{stats.diasPromedio}</p>
              <p className="text-xs text-ink-500 mt-1">Días promedio</p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white border border-ink-100 rounded-xl p-6">
          <h3 className="text-sm font-medium text-ink-500 uppercase tracking-wider mb-5">
            Estado del sistema
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-sm text-emerald-700">Sistema operativo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
              <span className="text-sm text-ink-600">Alertas pendientes</span>
              <span className={`text-sm font-medium ${stats.alertasCriticas > 0 ? 'text-amber-600' : 'text-ink-400'}`}>
                {stats.alertasCriticas}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
              <span className="text-sm text-ink-600">Tendencia</span>
              <span className={`text-sm font-medium ${tendenciaPositiva ? 'text-emerald-600' : 'text-red-600'}`}>
                {tendenciaPositiva ? 'Positiva' : 'Negativa'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Clients */}
      <div className="bg-white border border-ink-100 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-medium text-ink-500 uppercase tracking-wider">
            Top clientes
          </h3>
          <span className="text-xs text-ink-400">Por volumen de operaciones</span>
        </div>

        {stats.topClientes && stats.topClientes.length > 0 ? (
          <div className="space-y-2">
            {stats.topClientes.map((cliente, index) => (
              <button
                key={index}
                onClick={() => onClienteClick(cliente.nombre)}
                className="
                  w-full flex items-center justify-between
                  p-4 rounded-lg
                  hover:bg-ink-50
                  transition-colors duration-200
                  group text-left
                "
              >
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className={`
                    w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium
                    ${index === 0 ? 'bg-ink-900 text-white' :
                      index === 1 ? 'bg-ink-200 text-ink-700' :
                      index === 2 ? 'bg-ink-100 text-ink-600' :
                      'bg-ink-50 text-ink-500'}
                  `}>
                    {index + 1}
                  </div>

                  {/* Avatar */}
                  <div className="w-10 h-10 bg-ink-100 rounded-lg flex items-center justify-center">
                    <span className="text-ink-600 font-medium">
                      {cliente.nombre?.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div>
                    <p className="text-sm font-medium text-ink-800 group-hover:text-ink-900">
                      {cliente.nombre}
                    </p>
                    <p className="text-xs text-ink-400">
                      {cliente.totalContenedores} contenedores
                    </p>
                  </div>
                </div>

                {/* Metrics */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-ink-900">
                    {formatCurrency(cliente.totalVenta)}
                  </p>
                  <p className="text-xs text-emerald-600">
                    +{formatCurrency(cliente.utilidad)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="w-12 h-12 bg-ink-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Package size={20} className="text-ink-300" />
            </div>
            <p className="text-sm text-ink-500">No hay clientes registrados</p>
            <p className="text-xs text-ink-400 mt-1">Importa datos para comenzar</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
